/**
 * AccountGroupsCard — Manage Accounts + Groups panel (Feb 2026)
 *
 * User-defined folders that cluster connected social accounts across every
 * platform (Facebook Pages / Instagram / YouTube / LinkedIn / Pinterest /
 * Threads / TikTok / X / Reddit). Groups are purely organisational — real
 * OAuth tokens still live on the underlying account row.
 *
 * Mounted at the top of `/platforms` and (optionally) surfaced elsewhere.
 */
import React, { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Folder, FolderPlus, Loader2, Pencil, Trash2, X, Check } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface Group {
  group_id: string;
  workspace_id: string;
  name: string;
  color: "slate" | "indigo" | "fuchsia" | "emerald" | "amber" | "rose" | "sky" | "violet";
  sort_order: number;
  created_at: string;
  member_count?: number;
}

interface GroupsPayload {
  groups: Group[];
  unassigned_count: number;
}

interface Account {
  account_id: string;
  platform: string;
  display_name: string;
  handle?: string | null;
  group_ids?: string[];
}

const COLORS: Group["color"][] = ["slate", "indigo", "fuchsia", "emerald", "amber", "rose", "sky", "violet"];

const COLOR_CLASS: Record<Group["color"], { chip: string; ring: string; dot: string }> = {
  slate:    { chip: "bg-slate-50 text-slate-700 border-slate-200",     ring: "ring-slate-400",   dot: "bg-slate-400" },
  indigo:   { chip: "bg-indigo-50 text-indigo-700 border-indigo-200",  ring: "ring-indigo-400",  dot: "bg-indigo-500" },
  fuchsia:  { chip: "bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200", ring: "ring-fuchsia-400", dot: "bg-fuchsia-500" },
  emerald:  { chip: "bg-emerald-50 text-emerald-700 border-emerald-200", ring: "ring-emerald-400", dot: "bg-emerald-500" },
  amber:    { chip: "bg-amber-50 text-amber-700 border-amber-200",     ring: "ring-amber-400",   dot: "bg-amber-500" },
  rose:     { chip: "bg-rose-50 text-rose-700 border-rose-200",        ring: "ring-rose-400",    dot: "bg-rose-500" },
  sky:      { chip: "bg-sky-50 text-sky-700 border-sky-200",           ring: "ring-sky-400",     dot: "bg-sky-500" },
  violet:   { chip: "bg-violet-50 text-violet-700 border-violet-200",  ring: "ring-violet-400",  dot: "bg-violet-500" },
};

export const AccountGroupsCard: React.FC = () => {
  const qc = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [newColor, setNewColor] = useState<Group["color"]>("indigo");
  const [editing, setEditing] = useState<Group | null>(null);
  const [assigning, setAssigning] = useState<Group | null>(null);

  const q = useQuery<GroupsPayload>({
    queryKey: ["account-groups"],
    queryFn: async () => (await api.get("/account-groups")).data,
  });

  const create = useMutation({
    mutationFn: async () =>
      (await api.post("/account-groups", { name: newName.trim(), color: newColor, sort_order: 100 })).data,
    onSuccess: () => {
      toast.success("Group created");
      setCreateOpen(false);
      setNewName("");
      setNewColor("indigo");
      qc.invalidateQueries({ queryKey: ["account-groups"] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || "Create failed"),
  });

  const update = useMutation({
    mutationFn: async (g: Group) =>
      (await api.patch(`/account-groups/${g.group_id}`, { name: g.name, color: g.color })).data,
    onSuccess: () => {
      toast.success("Group updated");
      setEditing(null);
      qc.invalidateQueries({ queryKey: ["account-groups"] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || "Update failed"),
  });

  const del = useMutation({
    mutationFn: async (group_id: string) =>
      (await api.delete(`/account-groups/${group_id}`)).data,
    onSuccess: () => {
      toast.success("Group deleted");
      qc.invalidateQueries({ queryKey: ["account-groups"] });
      qc.invalidateQueries({ queryKey: ["catalog"] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || "Delete failed"),
  });

  const groups = q.data?.groups || [];

  return (
    <>
      <section
        data-testid="account-groups-card"
        className="mb-6 rounded-2xl border border-slate-200 bg-white p-5"
      >
        <div className="flex items-start justify-between gap-4 flex-wrap mb-3">
          <div>
            <div className="flex items-center gap-2 text-slate-900 font-semibold">
              <Folder className="h-4 w-4 text-indigo-600" />
              Manage Accounts + Groups
              <span className="inline-flex items-center rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px] font-semibold px-1.5 py-0.5 uppercase tracking-widest">
                New
              </span>
            </div>
            <p className="text-xs text-slate-500 mt-1 max-w-xl">
              Cluster your connected Facebook, Instagram, YouTube, LinkedIn, Pinterest, Threads, TikTok, X and Reddit accounts into folders you name yourself.
              Groups are organisational — they don&apos;t change what each account can post.
            </p>
          </div>
          <Button
            data-testid="account-group-create-btn"
            onClick={() => setCreateOpen(true)}
            className="h-9 gap-2"
          >
            <FolderPlus className="h-4 w-4" />
            New group
          </Button>
        </div>

        {q.isLoading ? (
          <div className="flex items-center gap-2 text-slate-500 text-sm mt-3">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading groups…
          </div>
        ) : groups.length === 0 ? (
          <div
            data-testid="account-groups-empty"
            className="text-center py-8 text-sm text-slate-500 border-2 border-dashed border-slate-200 rounded-xl"
          >
            No groups yet — create one to organise your {q.data?.unassigned_count ?? 0} connected accounts into folders.
          </div>
        ) : (
          <div className="flex flex-wrap gap-2" data-testid="account-groups-list">
            {groups.map((g) => {
              const c = COLOR_CLASS[g.color] || COLOR_CLASS.slate;
              return (
                <div
                  key={g.group_id}
                  data-testid={`group-chip-${g.group_id}`}
                  className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm font-medium ${c.chip}`}
                >
                  <span className={`h-2 w-2 rounded-full ${c.dot}`} />
                  <span>{g.name}</span>
                  <span className="text-[10px] font-mono opacity-70">{g.member_count ?? 0}</span>
                  <button
                    type="button"
                    data-testid={`group-assign-${g.group_id}`}
                    className="ml-1 rounded-full p-0.5 hover:bg-black/5"
                    onClick={() => setAssigning(g)}
                    title="Assign accounts to this group"
                  >
                    <Check className="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    data-testid={`group-edit-${g.group_id}`}
                    className="rounded-full p-0.5 hover:bg-black/5"
                    onClick={() => setEditing(g)}
                    title="Rename or recolor"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </button>
                  <button
                    type="button"
                    data-testid={`group-delete-${g.group_id}`}
                    className="rounded-full p-0.5 hover:bg-black/5"
                    onClick={() => {
                      if (confirm(`Delete group "${g.name}"? Members will remain connected but be un-grouped.`)) {
                        del.mutate(g.group_id);
                      }
                    }}
                    title="Delete group"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              );
            })}
            {(q.data?.unassigned_count ?? 0) > 0 && (
              <div
                data-testid="unassigned-chip"
                className="inline-flex items-center gap-1.5 rounded-full border border-dashed border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-500"
              >
                Unassigned <span className="text-[10px] font-mono">{q.data?.unassigned_count}</span>
              </div>
            )}
          </div>
        )}
      </section>

      {/* Create group modal */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent data-testid="group-create-modal" className="max-w-sm">
          <DialogHeader>
            <DialogTitle>New account group</DialogTitle>
            <DialogDescription>Give the folder a name and a color to make it easy to spot at a glance.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1 block">Name</label>
              <Input
                data-testid="group-create-name"
                autoFocus
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="e.g. Spring Campaign, Founder-owned, US Team"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1 block">Color</label>
              <div className="flex flex-wrap gap-2">
                {COLORS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    data-testid={`group-create-color-${c}`}
                    onClick={() => setNewColor(c)}
                    className={`h-7 w-7 rounded-full ring-2 ring-offset-2 transition ${
                      newColor === c ? COLOR_CLASS[c].ring : "ring-transparent"
                    } ${COLOR_CLASS[c].dot}`}
                  />
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button
              data-testid="group-create-submit"
              onClick={() => create.mutate()}
              disabled={!newName.trim() || create.isPending}
            >
              {create.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit group modal */}
      {editing && (
        <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
          <DialogContent data-testid="group-edit-modal" className="max-w-sm">
            <DialogHeader>
              <DialogTitle>Rename &amp; recolor</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <Input
                data-testid="group-edit-name"
                autoFocus
                value={editing.name}
                onChange={(e) => setEditing({ ...editing, name: e.target.value })}
              />
              <div className="flex flex-wrap gap-2">
                {COLORS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    data-testid={`group-edit-color-${c}`}
                    onClick={() => setEditing({ ...editing, color: c })}
                    className={`h-7 w-7 rounded-full ring-2 ring-offset-2 transition ${
                      editing.color === c ? COLOR_CLASS[c].ring : "ring-transparent"
                    } ${COLOR_CLASS[c].dot}`}
                  />
                ))}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
              <Button
                data-testid="group-edit-submit"
                onClick={() => update.mutate(editing)}
                disabled={!editing.name.trim() || update.isPending}
              >
                {update.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Assign accounts modal */}
      {assigning && <AssignAccountsModal group={assigning} onClose={() => setAssigning(null)} />}
    </>
  );
};

// ---- Assign accounts to a group ---------------------------------------------

interface CatalogAccount {
  account_id: string;
  platform: string;
  account_type: string;
  display_name: string;
  handle?: string | null;
  group_ids?: string[];
}

const AssignAccountsModal: React.FC<{ group: Group; onClose: () => void }> = ({ group, onClose }) => {
  const qc = useQueryClient();
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [seeded, setSeeded] = useState(false);

  // Reuse the /platforms/catalog payload so we don't add another endpoint.
  const q = useQuery<{ items: Array<{ platform: string; connected_accounts: Account[]; title: string }> }>({
    queryKey: ["catalog"],
    queryFn: async () => (await api.get("/platforms/catalog")).data,
  });

  const flatAccounts: CatalogAccount[] = useMemo(() => {
    const rows: CatalogAccount[] = [];
    for (const it of q.data?.items || []) {
      for (const a of it.connected_accounts || []) {
        rows.push({
          account_id: a.account_id,
          platform: a.platform,
          account_type: (a as any).account_type,
          display_name: a.display_name,
          handle: a.handle,
          group_ids: (a as any).group_ids || [],
        });
      }
    }
    return rows;
  }, [q.data]);

  // Seed selection with the group's current members on first load.
  if (!seeded && flatAccounts.length > 0) {
    const seed = new Set<string>();
    for (const a of flatAccounts) {
      if ((a.group_ids || []).includes(group.group_id)) seed.add(a.account_id);
    }
    setSelected(seed);
    setSeeded(true);
  }

  const save = useMutation({
    mutationFn: async () =>
      (await api.patch(`/account-groups/${group.group_id}/assign`, { account_ids: Array.from(selected) })).data,
    onSuccess: (data) => {
      toast.success(`${data.member_count} account(s) in "${group.name}"`);
      qc.invalidateQueries({ queryKey: ["account-groups"] });
      qc.invalidateQueries({ queryKey: ["catalog"] });
      onClose();
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || "Assign failed"),
  });

  const toggle = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="group-assign-modal" className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            Assign to <span className={`px-2 py-0.5 rounded-full text-sm ${COLOR_CLASS[group.color].chip}`}>{group.name}</span>
          </DialogTitle>
          <DialogDescription>Pick which connected accounts belong to this folder. Anything you deselect will be removed from this group.</DialogDescription>
        </DialogHeader>
        <div className="max-h-72 overflow-y-auto space-y-1.5 py-2" data-testid="group-assign-list">
          {q.isLoading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : flatAccounts.length === 0 ? (
            <div className="text-sm text-slate-500 py-6 text-center">
              No connected accounts yet. Connect one from the platform cards below and come back.
            </div>
          ) : (
            flatAccounts.map((a) => (
              <button
                key={a.account_id}
                type="button"
                data-testid={`assign-toggle-${a.account_id}`}
                onClick={() => toggle(a.account_id)}
                className={`w-full flex items-center justify-between gap-3 px-3 py-2 rounded-lg border transition text-left ${
                  selected.has(a.account_id)
                    ? "border-indigo-300 bg-indigo-50/50"
                    : "border-slate-200 hover:border-slate-300"
                }`}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-[10px] uppercase tracking-widest text-slate-500 font-mono flex-shrink-0">{a.platform}</span>
                  <span className="text-sm truncate">{a.display_name}</span>
                  {a.handle && <span className="text-xs text-slate-500 truncate">· {a.handle}</span>}
                </div>
                {selected.has(a.account_id) ? (
                  <Check className="h-4 w-4 text-indigo-600 flex-shrink-0" />
                ) : (
                  <X className="h-4 w-4 text-slate-300 flex-shrink-0" />
                )}
              </button>
            ))
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="group-assign-submit" onClick={() => save.mutate()} disabled={save.isPending}>
            {save.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : `Save (${selected.size})`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
